import log from 'loglevel';
import React, { useContext } from 'react';

import type { StandardizedUser } from '../types/types';

const UserContext = React.createContext<StandardizedUser | null>(null);

const emptyUser: StandardizedUser = {
    attributes: {
        sub: '',
        email: '',
        given_name: '',
        family_name: '',
        admin: false,
        canGrant: false,
        orgAdmin: false,
        numEmailAddresses: 1,
    },
    type: 'unauthenticated',
};

function useUserContext(): StandardizedUser {
    const ctx = useContext(UserContext);
    return ctx || emptyUser;
}

// See https://bobbyhadz.com/blog/aws-cognito-user-attributes
// for instructions on setting the custom:Admin property in cognito

// NB - the admin and canGrant custom attributes are only reset on login

// v6 fetchUserAttributes() returns a flat object like { sub, email, given_name, 'custom:Admin', ... }
type CognitoAttributes = Record<string, string | undefined>;

function standardizeUser(user: CognitoAttributes | { type: string } | { username: string } | null): StandardizedUser {
    if (!user) return emptyUser;
    const standardUser: Partial<StandardizedUser> = {};
    if ('sub' in user && (user as CognitoAttributes).sub) { // v6 attributes from fetchUserAttributes()
        const attrs = user as CognitoAttributes;
        standardUser.attributes = {
            sub: attrs.sub!,
            email: attrs.email!,
            given_name: attrs.given_name || '',
            family_name: attrs.family_name || '',
            admin: attrs['custom:Admin'] === '1',
            canGrant: attrs['custom:canGrant'] === '1',
            orgAdmin: attrs['custom:OrgAdmin'] === '1',
            numEmailAddresses: attrs['custom:numEmailAddresses']
                ? parseInt(attrs['custom:numEmailAddresses'], 10)
                : 1,
        };
        standardUser.type = 'Cognito';
    } else if ('username' in user) { // unconfirmed Cognito user
        standardUser.attributes = {
            sub: '',
            email: (user as { username: string }).username,
            given_name: '',
            family_name: '',
            admin: false,
            canGrant: false,
            orgAdmin: false,
            numEmailAddresses: 1,
        };
    } else if ('type' in user) {
        // unauthenticated user
        standardUser.type = (user as { type: string }).type;
    } else {
        log.error('Unexpected user type');
    }
    return standardUser as StandardizedUser;
}

export default useUserContext;

export {
    standardizeUser,
    UserContext,
};
